import SuperFetch from 'library/helpers/superFetch';
import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { getApiError } from 'library/helpers/getApiError';
import { Notification } from 'zComponents/atoms';
import { partnerAdminActions } from './slice';

// The staff gate (developerCheck) denies with a raw 401 whose code is NESTED under `error` — not a
// top-level statusCode — so a plain `statusCode === 403` check never fires. Treat either an auth (401)
// or authorisation (403) signal, in top-level OR nested form, as the quiet "not staff" degrade.
const isDenied = (res) => [res?.statusCode, res?.error?.statusCode].some((c) => c === 401 || c === 403);

// Org-LESS, staff-gated boundary (/v5/partner-admin, no /:orgID). Denial degrades quietly to `forbidden`
// (the page already self-gates on the developer flag); anything else non-200 is a real error.
export function* getPartners() {
	try {
		const res = yield call(() => SuperFetch.getWithoutOrg('/partner-admin/partners'));
		if (res.statusCode === 200) {
			yield put(partnerAdminActions.loadPartnersSuccess(res.data || []));
		} else if (isDenied(res)) {
			yield put(partnerAdminActions.loadPartnersForbidden());
		} else {
			throw Error(getApiError(res) || 'Failed to load partners.');
		}
	} catch (error) {
		yield put(partnerAdminActions.loadPartnersError(error.message || String(error)));
	}
}

export function* getPartnerDetail({ payload }) {
	try {
		const res = yield call(() => SuperFetch.getWithoutOrg(`/partner-admin/partners/${payload.partnerID}`));
		if (res.statusCode === 200) {
			yield put(partnerAdminActions.loadPartnerDetailSuccess(res.data || null));
		} else {
			throw Error(getApiError(res) || 'Failed to load partner.');
		}
	} catch (error) {
		yield put(partnerAdminActions.loadPartnerDetailError(error.message || String(error)));
	}
}

// Shared write runner: perform, notify, reload the list; reload the open detail too when a partner is
// named (member add/remove change that partner's roster + the list's member counts).
function* runMutation(fn, successMsg, reloadPartnerID) {
	try {
		const res = yield call(fn);
		if (res.statusCode === 200 || res.statusCode === 201) {
			Notification('success', successMsg);
			yield put(partnerAdminActions.loadPartners());
			if (reloadPartnerID) yield put(partnerAdminActions.loadPartnerDetail({ partnerID: reloadPartnerID }));
		} else {
			Notification('error', getApiError(res) || 'Partner update failed.');
		}
	} catch (error) {
		Notification('error', error.message || 'Partner update failed.');
	} finally {
		yield put(partnerAdminActions.mutateComplete());
	}
}

// Create a partner and, optionally, immediately send the master invite to the contact email in the same
// flow. `sendInvite` is a CLIENT-ONLY control flag — it is stripped before the POST so the strict Joi
// createPartner schema never sees an unknown key (which would 400 the whole request). The invite is a
// non-fatal follow-on: a failed invite never masks a successful create — the partner still exists and the
// invite can be resent from its page.
export function* createPartner({ payload }) {
	// BOTH client-only keys must be destructured off before `body` is POSTed — createPartner's schema is
	// strict-unknown-key, so a stray `inviteGrantsInstaller` would 400 the entire create, not just the
	// invite. They ride the same action because the designation belongs to the invite this chains.
	const { sendInvite = false, inviteGrantsInstaller = false, ...body } = payload;
	try {
		const res = yield call(() => SuperFetch.postWithoutOrg('/partner-admin/partners', body));
		if (res.statusCode === 200 || res.statusCode === 201) {
			Notification('success', `Partner '${body.name}' created.`);
			const newPartnerID = res.data?.partnerID ?? res.data?.partner?.partnerID;
			if (sendInvite && body.contactEmail) {
				if (newPartnerID) {
					const inv = yield call(() =>
						SuperFetch.postWithoutOrg(`/partner-admin/partners/${newPartnerID}/invites`, {
							email: body.contactEmail,
							isAdmin: true,
							grantsInstaller: inviteGrantsInstaller
						})
					);
					if (inv.statusCode === 200 || inv.statusCode === 201)
						Notification('success', `Master invite sent to ${body.contactEmail}.`);
					else
						Notification(
							'warning',
							getApiError(inv) ||
								`Partner created, but the invite to ${body.contactEmail} could not be sent — send it from the partner's page.`
						);
				} else {
					Notification(
						'warning',
						"Partner created, but the invite could not be auto-sent — send it from the partner's page."
					);
				}
			}
			yield put(partnerAdminActions.loadPartners());
		} else {
			Notification('error', getApiError(res) || 'Partner update failed.');
		}
	} catch (error) {
		Notification('error', error.message || 'Partner update failed.');
	} finally {
		yield put(partnerAdminActions.mutateComplete());
	}
}

export function* addMember({ payload }) {
	const { partnerID, email } = payload;
	yield* runMutation(
		() => SuperFetch.postWithoutOrg(`/partner-admin/partners/${partnerID}/members`, { email }),
		`${email} added to the partner.`,
		partnerID
	);
}

export function* removeMember({ payload }) {
	const { partnerID, membershipID } = payload;
	yield* runMutation(
		() => SuperFetch.deleteWithoutOrg(`/partner-admin/partners/${partnerID}/members/${membershipID}`),
		'Member removed.',
		partnerID
	);
}

export function* inviteMaster({ payload }) {
	// grantsInstaller defaults FALSE here as it does server-side — the opposite of isAdmin, which
	// defaults true because this is the master-account invite. Org-create authority must be asked for,
	// never inherited from "this is the master account".
	const { partnerID, email, isAdmin = true, grantsInstaller = false } = payload;
	yield* runMutation(
		() =>
			SuperFetch.postWithoutOrg(`/partner-admin/partners/${partnerID}/invites`, {
				email,
				isAdmin,
				grantsInstaller
			}),
		`Invite sent to ${email}.`,
		partnerID
	);
}

export function* resendInvite({ payload }) {
	const { partnerID, inviteID } = payload;
	yield* runMutation(
		() => SuperFetch.postWithoutOrg(`/partner-admin/partners/${partnerID}/invites/${inviteID}/resend`, {}),
		'Invite resent.',
		partnerID
	);
}

export function* revokeInvite({ payload }) {
	const { partnerID, inviteID } = payload;
	yield* runMutation(
		() => SuperFetch.deleteWithoutOrg(`/partner-admin/partners/${partnerID}/invites/${inviteID}`),
		'Invite revoked.',
		partnerID
	);
}

export function* setMemberAdmin({ payload }) {
	const { partnerID, membershipID, isAdmin } = payload;
	yield* runMutation(
		() =>
			SuperFetch.patchWithoutOrg(`/partner-admin/partners/${partnerID}/members/${membershipID}/admin`, {
				isAdmin
			}),
		isAdmin ? 'Member promoted to admin.' : 'Admin rights removed.',
		partnerID
	);
}

export default function* partnerAdminSaga() {
	yield takeLatest(partnerAdminActions.loadPartners, getPartners);
	yield takeLatest(partnerAdminActions.loadPartnerDetail, getPartnerDetail);
	// Writes use takeEvery — two quick removes/adds must both run; takeLatest would cancel the first
	// (firing its `finally` mutateComplete mid-flight) and drop its reload.
	yield takeEvery(partnerAdminActions.createPartner, createPartner);
	yield takeEvery(partnerAdminActions.addMember, addMember);
	yield takeEvery(partnerAdminActions.removeMember, removeMember);
	yield takeEvery(partnerAdminActions.inviteMaster, inviteMaster);
	yield takeEvery(partnerAdminActions.resendInvite, resendInvite);
	yield takeEvery(partnerAdminActions.revokeInvite, revokeInvite);
	yield takeEvery(partnerAdminActions.setMemberAdmin, setMemberAdmin);
}
